import React, { CSSProperties, useState, useRef } from 'react';
import styles from './house.module.scss';
import posed, { PoseGroup } from 'react-pose';
import useOutsideClick from '../../hooks/use-outside-click';
import { Button } from '../button';

interface Props {
  title?: string;
  content?: string;
  link?: string;
  coords: {
    x: number;
    y: number;
  };
}

const Content = posed.div({
  enter: {
    opacity: 1,
    transform: 'translateY(0px)',
    pointerEvents: 'auto',
  },
  exit: {
    opacity: 0,
    transform: 'translateY(10px)',
    pointerEvents: 'none',
  },
});

export const HouseMarker: React.FC<Props> = React.forwardRef<HTMLDivElement, Props>(
  ({ coords: { x, y }, title, content, link }, ref) => {
    // State
    const [contentVisible, setContentVisible] = useState(false);

    const contentRef = useRef<HTMLDivElement>(null);

    // Position based on x/y props
    const style: CSSProperties = {
      gridColumn: `${x} / span 1`,
      gridRow: `${y} / span 1`,
      top: `${y}%`,
      left: `${x}%`,
    };

    // Toggle the state of the content
    const toggleContent = () => {
      // Tracking
      if (!contentVisible) {
        window.digitalData.tool.name = 'PWS House Map';
        window.digitalData.tool.step = title;
        window.digitalData.tool.markerType = 'household';
        window.dataLayer.push({
          event: 'mapInteraction',
        });
      }

      setContentVisible(!contentVisible);
    };
    useOutsideClick(contentRef, () => {
      setContentVisible(false);
    });

    const openClass = contentVisible ? styles.open : '';

    return (
      <div className={`${styles.marker} ${openClass}`} style={style} ref={ref}>
        <div ref={contentRef}>
          <PoseGroup>
            {contentVisible && (
              <Content key="content">
                <div className={styles.markercontent}>
                  <h3>{title}</h3>
                  <p>{content}</p>
                  {link && (
                    <Button href={link} small>
                      Find out more
                    </Button>
                  )}
                  <button className={styles.closeButton} onClick={toggleContent} aria-label="close" />
                </div>
              </Content>
            )}
          </PoseGroup>
          <button className={styles.markerpin} onClick={toggleContent} />
        </div>
      </div>
    );
  }
);
